/* eslint-disable max-len */
import styled from 'styled-components';
import { AllieCard } from '../../../components/Card';
import { AllieText } from '../../../components/Text';
import { AllieColors } from '../../../common/color.types';
import { MachineStatusType } from './SnakeContainer.types';

const desktopXlScreen = window
  .getComputedStyle(document.documentElement)
  .getPropertyValue('--allie-xl-screen');
const desktopXxlScreen = window
  .getComputedStyle(document.documentElement)
  .getPropertyValue('--allie-xxl-screen');

export const StyledCard = styled(AllieCard)`
  border-radius: 0;
  box-shadow: none;
  background: none;
  margin: 12px 0 0 0;

  ion-card-content {
    padding: 0;
  }
`;

export const SnakeMachinesContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? '30px' : '24px')};
  padding: ${({ isMobile }) => (isMobile ? '8px' : '8px 24px 8px 26px')};
  @media (min-width: ${desktopXlScreen}) {
    gap: 34px;
  }
`;

export const ItemWrapper = styled.div<{ isLastItem: boolean; isEmpty: boolean; isMobile: boolean }>`
  position: ${(props) => (!props.isLastItem ? 'relative' : 'none')};
  display: flex;
  align-items: center;
  width: ${(props) => {
    if (props.isMobile) return '100%';
    if (props.isEmpty) return '210px';
    return 'initial';
  }};
  height: ${({ isMobile }) => (isMobile ? '100%' : '163px')};

  @media (min-width: ${desktopXlScreen}) {
    width: ${(props) => props.isEmpty && '210px'};
    height: 165px;
  }

  @media (min-width: ${desktopXxlScreen}) {
    width: ${(props) => props.isEmpty && '250px'};
    height: 185px;
  }
`;

export const SnakeRow = styled.div`
  display: flex;
  width: 100%;
  gap: 30px;
`;

export const SnakeArrow = styled.div<{ isLastItem?: boolean; isEmpty?: boolean }>`
  position: ${(props) => (props.isLastItem !== undefined ? 'absolute' : 'relative')};

  &.right {
    display: ${(props) => (props.isLastItem || props.isEmpty ? 'none' : 'block')};
    top: 50%;
    left: 102%;
    transform: translateY(-50%) rotate(0deg);
  }

  &.down {
    display: ${(props) => (props.isLastItem || props.isEmpty ? 'none' : 'block')};
    top: 100%;
    left: 50%;
    transform: translateX(-50%) rotate(90deg);
  }

  &.left {
    display: ${(props) => (props.isLastItem || props.isEmpty ? 'none' : 'block')};
    top: ${(props) => (props.isLastItem !== undefined ? '50%' : '10%')};
    right: 102%;
    transform: translateY(-50%) rotate(180deg);
  }
`;

const mapStatusBorderColor: Record<MachineStatusType, AllieColors | null> = {
  alert: 'error-main',
  normal: 'success-main',
  warning: 'warning-main',
  neutral: 'neutral-40',
  no_production: 'neutral-40',
};

export const SnakeContent = styled(AllieCard)<{
  status?: string;
  isEmpty: boolean;
  stateFilter: boolean;
  isStationActive: boolean;
  isMobile: boolean;
}>`
  z-index: 10;
  width: ${(props) => (props.isMobile ? '100%' : props.isEmpty ? '266px' : '210px')};
  height: ${(props) => (props.isMobile ? 'auto' : props.status === 'alert' ? '163px' : '162px')};
  display: flex;
  box-sizing: border-box;
  opacity: ${(props) => (props.isStationActive && !props.stateFilter ? 0.5 : 1)};
  border-radius: var(--allie-radius-xs);
  border-left: ${(props) =>
    !props.isEmpty &&
    (props.status && props.stateFilter
      ? `6px solid var(--allie-color-${mapStatusBorderColor[props.status as MachineStatusType]})`
      : `6px solid var(--allie-color-${mapStatusBorderColor.neutral})`)};
  border-top: ${(props) =>
    props.status === 'alert' &&
    (props.stateFilter
      ? `2px solid var(--allie-color-${mapStatusBorderColor[props.status]})`
      : `2px solid var(--allie-color-${mapStatusBorderColor.neutral})`)};
  border-right: ${(props) =>
    props.status === 'alert' &&
    (props.stateFilter
      ? `2px solid var(--allie-color-${mapStatusBorderColor[props.status]})`
      : `2px solid var(--allie-color-${mapStatusBorderColor.neutral})`)};
  border-bottom: ${(props) =>
    props.status === 'alert' &&
    (props.stateFilter
      ? `2px solid var(--allie-color-${mapStatusBorderColor[props.status]})`
      : `2px solid var(--allie-color-${mapStatusBorderColor.neutral})`)};
  box-shadow: ${(props) =>
    !props.isEmpty
      ? '0px 1px 3px 0px rgba(16, 24, 40, 0.1), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)'
      : 'none'};
  background: ${(props) => (!props.isEmpty ? '#fff' : 'transparent')};
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0;

  ion-card-content {
    padding: ${({ isMobile }) => (isMobile ? '0 20px' : '8px 8px 8px 12px')};
    height: 100%;
    width: -webkit-fill-available;
  }

  @media (min-width: ${desktopXlScreen}) {
    width: ${(props) => (props.isEmpty ? '216px' : '211px')};
    height: 167px;
  }

  @media (min-width: ${desktopXxlScreen}) {
    width: ${(props) => (props.isEmpty ? '255px' : '250px')};
    height: 187px;
  }
`;

export const StatusIcon = styled.div<{ background: string; color: string }>`
  width: 20px;
  height: 20px;
  background: ${(props) => props.background};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--allie-radius-md);

  & ion-icon {
    font-size: 12px;
    color: ${(props) => props.color};
  }

  & div {
    width: 12px;
    height: 12px;
    display: flex;
    align-items: center;
  }
`;

export const VariableConfig = styled.div<{ background: string }>`
  height: 24px;
  width: 100%;
  max-width: 24px;
  background: ${(props) => props.background};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--allie-radius-full);

  & ion-icon {
    font-size: 14px;
    color: ${(props) => props.color};
  }

  & div {
    width: 13px;
    height: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (min-width: ${desktopXlScreen}) {
    max-width: 36px;
    height: 36px;

    & ion-icon {
      font-size: 20px;
    }

    & div {
      width: 20px;
      height: 20px;
      svg {
        width: 20px;
        height: 20px;
      }
    }
  }
`;

export const MachineNameText = styled(AllieText)<{ isMobile: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-size: ${({ isMobile }) => (isMobile ? '16px' : '14px')} !important;
  line-height: ${({ isMobile }) => (isMobile ? '22.4px' : '20px')} !important;
  font-weight: 600 !important;
  width: fit-content;

  @media (min-width: ${desktopXlScreen}) {
    font-size: 16px !important;
    line-height: 22px !important;
  }
`;

export const MachineStatusText = styled(AllieText)<{ isMobile: boolean }>`
  font-size: 12px !important;
  line-height: 18px !important;
  white-space: nowrap;

  @media (min-width: ${desktopXxlScreen}) {
    font-size: 14px !important;
    line-height: 20px !important;
  }
`;

export const MachineLastUpdateText = styled(AllieText)<{ isMobile: boolean }>`
  font-size: ${({ isMobile }) => (isMobile ? '10px' : '10px')} !important;
  line-height: normal !important;
  white-space: nowrap;

  @media (min-width: ${desktopXxlScreen}) {
    font-size: 12px !important;
    line-height: 16px !important;
  }
`;

export const MachineVariableStatusText = styled(AllieText)<{ isMobile: boolean }>`
  font-size: 8px !important;
  font-weight: 500 !important;
  line-height: normal !important;

  @media (min-width: ${desktopXxlScreen}) {
    font-size: 12px !important;
  }
`;

export const MachineVariableText = styled(AllieText)<{ isMobile: boolean }>`
  font-size: 12px !important;
  line-height: 16px !important;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  display: block;

  @media (min-width: ${desktopXlScreen}) {
    font-size: 14px !important;
    line-height: 22px !important;
    width: 250px;
  }
  @media (min-width: ${desktopXxlScreen}) {
    width: 300px;
  }
`;

export const MachineVariableValue = styled(AllieText)<{ isMobile: boolean }>`
  font-size: 12px !important;
  line-height: 16px !important;
  white-space: nowrap;
  text-align: right;

  @media (min-width: ${desktopXlScreen}) {
    font-size: 14px !important;
    line-height: 22px !important;
    width: 250px;
  }
  @media (min-width: ${desktopXxlScreen}) {
    width: 300px;
  }
`;

export const MachineVariableBadge = styled(AllieText)`
  font-size: 10px !important;
  font-weight: 500 !important;

  @media (min-width: ${desktopXxlScreen}) {
    font-size: 14px !important;
    line-height: 20px !important;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  display: flex;
  z-index: 1000;
`;

export const ModalContent = styled(AllieCard)`
  width: 100%;
  max-width: 360px;
  border-radius: var(--allie-radius-xl);
  margin: 0;

  ion-card-header {
    background: var(--allie-color-neutral-10);
  }
  ion-card-content {
    display: flex;
    flex-direction: column;
  }
`;

export const VariableList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-top: var(--allie-spacing-md);
  margin-bottom: var(--allie-spacing-md);
`;

export const VariableItem = styled.div`
  display: flex;
  gap: var(--allie-spacing-md);
  align-items: center;
  padding: var(--allie-spacing-xs) 0;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--allie-spacing-md);
`;

export const FeedbackIcon = styled.div<{ success: boolean | null }>`
  display: flex;
  width: 44px;
  padding: var(--allie-spacing-xl);
  margin-top: var(--allie-spacing-xl);
  justify-content: center;
  align-items: center;
  gap: var(--allie-spacing-md);
  border-radius: var(--allie-radius-full);
  background: ${({ success }) =>
    success ? 'var(--allie-color-success-main)' : 'var(--allie-color-error-main)'};
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  color: var(--allie-color-white);

  ion-icon {
    font-size: 44px;
  }
`;
